import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { SITE } from "@/config/site";
import { Reveal, Kicker, SectionTitle } from "./motion";

const brl = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const BAD = ["Semanas criando e diagramando atividades", "Pagar designer ou ferramentas", "Pouco material para oferecer", "Demora para fazer a primeira venda"];
const GOOD = ["+25.000 materiais prontos no primeiro dia", "Licença de revenda inclusa", "Dezenas de kits possíveis por tema e idade", "Comece a divulgar no mesmo dia"];

const Simulator = () => {
  const [n, setN] = useState(1);
  const total = Math.round(n * SITE.kitPrice * 30);
  return (
    <div className="rounded-[32px] border border-white/10 bg-white/[0.04] p-7 backdrop-blur-xl sm:p-10" data-testid="income-simulator">
      <p className="text-sm font-semibold uppercase tracking-widest text-violet-200">Vendas por dia</p>
      <div className="mt-2 flex items-baseline gap-3">
        <span className="font-display text-6xl font-extrabold text-white" data-testid="income-sales-per-day">{n}</span>
        <span className="text-zinc-300">{n === 1 ? "venda" : "vendas"} de {SITE.kitPrice.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</span>
      </div>
      <Slider value={[n]} min={1} max={10} step={1} onValueChange={(v) => setN(v[0])} className="mt-6 sim-slider" data-testid="income-simulator-slider" />
      <div className="mt-8 border-t border-white/10 pt-6">
        <p className="text-sm text-zinc-300">Em 30 dias, isso seria:</p>
        <div className="h-[72px] overflow-hidden sm:h-[88px]">
          <AnimatePresence initial={false}>
            <motion.p key={total} initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -40, opacity: 0, position: "absolute" }}
              transition={{ duration: 0.4 }} className="font-display text-6xl font-extrabold text-sun sm:text-7xl" data-testid="income-monthly-total">
              {brl(total)}
            </motion.p>
          </AnimatePresence>
        </div>
        <p className="text-sm text-zinc-300">por mês</p>
      </div>
    </div>
  );
};

const Col = ({ title, items, good }) => (
  <div className={`rounded-[28px] p-7 sm:p-8 ${good ? "bg-sun text-ink" : "border border-white/10 text-white"}`} data-testid={good ? "compare-good" : "compare-bad"}>
    <h3 className="font-display text-xl font-extrabold sm:text-2xl">{title}</h3>
    <ul className="mt-5 space-y-3">
      {items.map((t) => (
        <li key={t} className="flex items-start gap-3 text-base">
          {good ? <Check className="mt-0.5 h-5 w-5 shrink-0 text-plum" /> : <X className="mt-0.5 h-5 w-5 shrink-0 text-rose" />}
          <span className={good ? "font-semibold" : "text-zinc-300"}>{t}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const IncomeMath = () => (
  <section id="contas" className="relative overflow-hidden bg-night py-20 text-white sm:py-28" data-testid="income-section">
    <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-plum/40 blur-[120px]" />
    <div className="relative mx-auto max-w-6xl px-5">
      <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <Kicker dark>Faça as contas</Kicker>
          <SectionTitle className="mt-5">E se você vender <span className="text-sun">todo dia?</span></SectionTitle>
          <p className="mt-5 max-w-md text-base text-zinc-300 sm:text-lg">Simulação com kits de {SITE.kitPrice.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} em 30 dias. E quem define o preço é você.</p>
          <p className="mt-6 max-w-md text-xs leading-relaxed text-zinc-400">*Exemplo ilustrativo. O preço é definido por você e os resultados dependem da sua dedicação e divulgação. Não há garantia de ganhos.</p>
        </Reveal>
        <Reveal delay={0.1}><Simulator /></Reveal>
      </div>
      <div className="mt-16 grid gap-4 md:grid-cols-2">
        <Reveal><Col title="Criar um produto do zero" items={BAD} /></Reveal>
        <Reveal delay={0.1}><Col title={`Com ${SITE.name}`} items={GOOD} good /></Reveal>
      </div>
    </div>
  </section>
);
